/**
 * 支付助手视觉适配（仅 officialTopupNew 宿主侧）
 *
 * 背景：
 * - 充值中心竖屏会把 meta viewport 设为 width=750
 * - 整页按 750 逻辑宽排版，再缩放到真机屏上；SDK 浮球/弹层也落在这套坐标系里
 * - Chat App 按普通手机逻辑宽写固定 px；宿主 iframe 被撑到 ~750 后内容会显小
 *
 * 做法：
 * - contentScale ≈ 布局宽 / 375（如 750/375 = 2），给对话 iframe 满补偿
 * - shellScale ≈ contentScale * 0.75（如 1.5）：浮球/气泡只做部分放大
 *   （满补偿会回到真机 ~80px 入口，相对 750 稿页面其它控件会显大）
 * - 浮球：CSS zoom（neodrag 占用 transform，不能再 scale；锚点由 SDK 按视觉矩形算）
 * - 气泡：transform:scale + transform-origin 贴锚点，避免 iOS Safari
 *   把 CSS zoom 作用到 position:fixed 的 left/top 上导致整块往左上飘
 * - 对话 iframe：按 375 坐标系设宽高再 transform 铺满外壳
 *
 * 目标：对话可读；入口/气泡别过小也不要比页面控件抢眼。
 *
 * 【兜底】apply 包 try/catch；MutationObserver 不扫整页 subtree，
 * 降低充值页 DOM 变动时的卡顿与异常放大风险。
 */

/** Chat App 对话按此逻辑宽做满补偿基准 */
const DESIGN_DEVICE_WIDTH = 375;
/**
 * 浮球/气泡相对 contentScale 的折扣
 * 750 页上 contentScale=2 时 shell≈1.5，入口视觉约 60px 量级，更贴近页内密度
 */
const SHELL_SCALE_RATIO = 0.75;
/** 小于该阈值视为无需补偿（device-width 宿主 / PC） */
const SCALE_EPS = 1.05;
/** 防止异常测量把 UI 放得过大 */
const SCALE_MAX = 3;
/** DOM 频繁变动时合并重算 */
const DEBOUNCE_MS = 50;

/**
 * 是否应对助手做设计稿放大（750→375 补偿）
 * PC 浏览器即使用 width=750 meta，也不放大，避免浮球/弹层巨大；
 * 真机触控（coarse + no hover）才补偿。
 */
function shouldApplyDesignScale() {
  if (typeof window === 'undefined') return false;
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const noHover = window.matchMedia('(hover: none)').matches;
  return coarse && noHover;
}

let started = false;
let debounceTimer = null;
let mutationObserver = null;
let resizeObserver = null;
/** 仅浮球：style 里 width/height 变化时 schedule（忽略 left/top 拖拽） */
let floatSizeObserver = null;
/** @type {HTMLElement|null} */
let observedFloatBtn = null;
/** 当前正在观察尺寸的 .agent-dialog-body */
let observedBody = null;
/** 已挂 subtree 监听的助手根节点 */
const observedRoots = new Set();
/** apply 期间及刚结束的一小段时间内，忽略 Observer 回调，避免改 style → 再 schedule 的死循环 */
let isApplyingAdapt = false;
let adaptSuppressUntil = 0;
/** 上次成功 apply 的 scale / 布局，用于短路 */
let lastAppliedContentScale = null;
let lastAppliedShellScale = null;
let lastAppliedLayoutWidth = null;
/** 视口事件去重：Safari visualViewport scroll 不改变 layout 宽，不必重算 */
let lastViewportAdaptKey = '';

/** 宿主当前布局视口宽（viewport=750 时约为 750） */
function getLayoutWidth() {
  return (
    document.documentElement.clientWidth ||
    window.innerWidth ||
    1
  );
}

/**
 * 读取 meta viewport 是否写死了数字宽（750/1280/1980）
 * width=device-width 或未写数字 → null（真机 PC/普通手机，不做设计稿补偿）
 * @returns {number|null}
 */
export function getHostFixedViewportWidth() {
  if (typeof document === 'undefined') return null;
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return null;
  const content = meta.getAttribute('content') || '';
  if (/width\s*=\s*device-width/i.test(content)) return null;
  const m = content.match(/width\s*=\s*(\d+)/i);
  if (!m) return null;
  const w = Number.parseInt(m[1], 10);
  return Number.isFinite(w) && w > 0 ? w : null;
}

/**
 * 仅「固定设计稿 viewport」时抬高移动端断点；PC device-width 返回 undefined 走 SDK 默认 768
 * @param {{ mobileBreakpoint?: number }} [config]
 * @returns {number|undefined}
 */
export function resolvePayAssistantMobileBreakpoint(config = {}) {
  if (
    typeof config.mobileBreakpoint === 'number' &&
    config.mobileBreakpoint > 0
  ) {
    return config.mobileBreakpoint;
  }
  const fixed = getHostFixedViewportWidth();
  // 覆盖手机 750、iPad 1280/1980；真 PC 不抬高，避免整页变移动端大抽屉
  if (fixed != null && fixed >= 750) {
    return Math.max(2000, fixed + 1);
  }
  return undefined;
}

/**
 * 对话 iframe 用的满补偿倍数；1 表示不适配
 * 仅 meta 固定设计稿宽时放大；PC device-width 宽屏不再误按 375 放大
 * @returns {number}
 */
export function getPayAssistantVisualScale() {
  const fixed = getHostFixedViewportWidth();
  if (fixed == null || fixed < 750) return 1;
  if (!shouldApplyDesignScale()) return 1;

  const layout = getLayoutWidth();
  if (!(layout > 0)) return 1;
  const scale = layout / DESIGN_DEVICE_WIDTH;
  if (!Number.isFinite(scale) || scale < SCALE_EPS) return 1;
  return Math.min(scale, SCALE_MAX);
}

/** 浮球/气泡用的较弱倍数，避免相对 750 页显过大 */
function getShellVisualScale(contentScale) {
  if (contentScale <= 1) return 1;
  const shell = contentScale * SHELL_SCALE_RATIO;
  return Math.max(1, Math.min(shell, SCALE_MAX));
}

let lastSafariDiagAt = 0;

function logSafariFloatDiag(reason, shellScale) {
  const now = Date.now();
  if (now - lastSafariDiagAt < 800) return;
  lastSafariDiagAt = now;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isSafari =
    /iPad|iPhone|iPod/i.test(ua) ||
    (/Safari/i.test(ua) &&
      !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS/i.test(ua));
  if (!isSafari) return;
  const el = document.querySelector('.agent-float-btn');
  if (!el) return;
  const vv = window.visualViewport;
  const rect = el.getBoundingClientRect();
  const tip = document.querySelector('.agent-tip-root');
  const tipRect =
    tip && !tip.hasAttribute('hidden') ? tip.getBoundingClientRect() : null;
  console.info('[AgentSafariDiag]', {
    reason,
    shellScale,
    clientWidth: document.documentElement.clientWidth,
    clientHeight: document.documentElement.clientHeight,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    vvWidth: vv && vv.width,
    vvHeight: vv && vv.height,
    vvOffsetLeft: vv && vv.offsetLeft,
    vvOffsetTop: vv && vv.offsetTop,
    scrollX: window.scrollX || document.documentElement.scrollLeft || 0,
    scrollY: window.scrollY || document.documentElement.scrollTop || 0,
    zoomStyle: el.style.zoom || '',
    zoomComputed: getComputedStyle(el).zoom,
    transformOrigin: getComputedStyle(el).transformOrigin,
    floatOffset: { w: el.offsetWidth, h: el.offsetHeight },
    floatRect: {
      left: Math.round(rect.left * 10) / 10,
      top: Math.round(rect.top * 10) / 10,
      right: Math.round(rect.right * 10) / 10,
      bottom: Math.round(rect.bottom * 10) / 10,
      width: Math.round(rect.width * 10) / 10,
      height: Math.round(rect.height * 10) / 10,
    },
    hasVvHost: !!el.closest('.agent-float-vv-host'),
    tipRect: tipRect
      ? {
          left: Math.round(tipRect.left * 10) / 10,
          top: Math.round(tipRect.top * 10) / 10,
          right: Math.round(tipRect.right * 10) / 10,
          bottom: Math.round(tipRect.bottom * 10) / 10,
          width: Math.round(tipRect.width * 10) / 10,
          height: Math.round(tipRect.height * 10) / 10,
        }
      : null,
  });
}

function isWebKitSafariHost() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return (
    /Safari/i.test(ua) &&
    !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS/i.test(ua)
  );
}

/**
 * Safari 真机：zoom=1.5 会把 position:fixed 的包含块缩成 1/zoom
 * （750×1128 → 500×752），浮球从右下跳到 (408,373)。
 * 改为写放大后的 width/height，坐标仍在 750 布局里。
 */
function adaptFloatButtonBySize(el, scale) {
  if (el.style.zoom) el.style.removeProperty('zoom');
  if (el.style.transformOrigin) el.style.removeProperty('transform-origin');
  const w = Number.parseFloat(el.style.width);
  const h = Number.parseFloat(el.style.height);
  if (!(w > 0) || !(h > 0)) return;

  const prevScale = Number.parseFloat(el.dataset.agentShellScale || '1') || 1;
  const prevBaseW = Number.parseFloat(el.dataset.agentBaseW || '');
  const prevBaseH = Number.parseFloat(el.dataset.agentBaseH || '');
  const alreadyScaled =
    prevBaseW > 0 &&
    Math.abs(w - prevBaseW * prevScale) < 1.5 &&
    Math.abs(h - prevBaseH * prevScale) < 1.5;
  const baseW = alreadyScaled ? prevBaseW : w;
  const baseH = alreadyScaled ? prevBaseH : h;

  if (scale <= 1) {
    if (!alreadyScaled) {
      delete el.dataset.agentShellScale;
      delete el.dataset.agentBaseW;
      delete el.dataset.agentBaseH;
      return;
    }
    const resetW = `${baseW}px`;
    const resetH = `${baseH}px`;
    if (el.style.width === resetW && el.style.height === resetH) {
      delete el.dataset.agentShellScale;
      delete el.dataset.agentBaseW;
      delete el.dataset.agentBaseH;
      return;
    }
    el.style.width = resetW;
    el.style.height = resetH;
    delete el.dataset.agentShellScale;
    delete el.dataset.agentBaseW;
    delete el.dataset.agentBaseH;
    return;
  }

  const nextW = `${Math.round(baseW * scale)}px`;
  const nextH = `${Math.round(baseH * scale)}px`;
  if (
    el.dataset.agentShellScale === String(scale) &&
    el.style.width === nextW &&
    el.style.height === nextH
  ) {
    return;
  }

  el.dataset.agentBaseW = String(baseW);
  el.dataset.agentBaseH = String(baseH);
  el.dataset.agentShellScale = String(scale);
  if (el.style.width !== nextW) el.style.width = nextW;
  if (el.style.height !== nextH) el.style.height = nextH;
}

/** 入口浮球：Chromium 用 zoom；Safari 改宽高，避免 fixed+zoom 缩坐标系 */
function adaptFloatButton(scale) {
  const el = document.querySelector('.agent-float-btn');
  if (!el) return;
  if (el.classList.contains('is-dragging')) return;
  if (isWebKitSafariHost()) {
    adaptFloatButtonBySize(el, scale);
    return;
  }
  const desiredZoom = scale === 1 ? '' : String(scale);
  const desiredOrigin = scale === 1 ? '' : '100% 50%';
  const curZoom = el.style.getPropertyValue('zoom');
  const curOrigin = el.style.getPropertyValue('transform-origin');
  if (curZoom === desiredZoom && curOrigin === desiredOrigin) {
    return;
  }
  if (scale === 1) {
    el.style.removeProperty('zoom');
    el.style.removeProperty('transform-origin');
    return;
  }
  el.style.zoom = desiredZoom;
  el.style.transformOrigin = desiredOrigin;
}

/** 按 floating-ui 的 placement，把放大原点贴在靠近浮球的那一侧 */
function getTipScaleOrigin(root) {
  const side = String(root.getAttribute('data-placement') || 'top').split(
    '-',
  )[0];
  const originBySide = {
    top: 'bottom right',
    bottom: 'top right',
    left: 'right center',
    right: 'left center',
  };
  return originBySide[side] || 'bottom right';
}

function clearTipScale(root, mount) {
  root.style.removeProperty('max-width');
  root.style.removeProperty('zoom');
  root.style.removeProperty('overflow');
  delete root.dataset.agentTipNudgeKey;
  if (!mount) return;
  mount.style.removeProperty('zoom');
  mount.style.removeProperty('transform');
  mount.style.removeProperty('transform-origin');
}

/** scale 后按 mount 视觉边界微调 root，避免顶/左/右被裁切 */
function nudgeTipRootIntoViewport(root, mount, scale) {
  if (!root || !mount) return;
  // tip 在 vvHost 内为 absolute，由 SDK clamp；勿按 fixed 视口坐标 nudge
  if (root.closest('.agent-float-vv-host')) return;

  const nudgeKey = `${scale}|${root.getAttribute('data-placement') || ''}`;
  if (root.dataset.agentTipNudgeKey === nudgeKey) return;

  const pad = 8;
  const vv = window.visualViewport;
  const vw = (vv && vv.width) || window.innerWidth;
  const vh = (vv && vv.height) || window.innerHeight;
  const vx = (vv && vv.offsetLeft) || 0;
  const vy = (vv && vv.offsetTop) || 0;

  const rect = mount.getBoundingClientRect();
  let dx = 0;
  let dy = 0;
  if (rect.left < vx + pad) dx = vx + pad - rect.left;
  else if (rect.right > vx + vw - pad) dx = vx + vw - pad - rect.right;
  if (rect.top < vy + pad) dy = vy + pad - rect.top;
  else if (rect.bottom > vy + vh - pad) dy = vy + vh - pad - rect.bottom;
  root.dataset.agentTipNudgeKey = nudgeKey;
  if (dx === 0 && dy === 0) return;

  const curLeft = Number.parseFloat(root.style.left);
  const curTop = Number.parseFloat(root.style.top);
  if (Number.isFinite(curLeft)) {
    const nextLeft = `${Math.round(curLeft + dx)}px`;
    if (root.style.left !== nextLeft) root.style.left = nextLeft;
  }
  if (Number.isFinite(curTop)) {
    const nextTop = `${Math.round(curTop + dy)}px`;
    if (root.style.top !== nextTop) root.style.top = nextTop;
  }
}

/**
 * 入口/业务气泡：放大 .agent-tip-mount
 * - 不给 .agent-tip-root 加 zoom：SDK 用 floating-ui 写 left/top，并按祖先 zoom 除一次，挂在 root 上会偏位
 * - 也不给 mount 加 CSS zoom：iOS Safari 会把 zoom 原点算到视口左上，气泡离浮球很远
 * - 改用 transform:scale，原点贴锚点（top → bottom right），布局宽不变、视觉往远离屏幕边缘的方向长
 */
function adaptTipBubble(scale) {
  const root = document.querySelector('.agent-tip-root');
  if (!root) return;

  const mount =
    root.querySelector('.agent-tip-mount') ||
    root.querySelector('.agent-tip') ||
    null;

  if (scale === 1) {
    clearTipScale(root, mount);
    return;
  }

  if (mount) {
    const desiredOrigin = getTipScaleOrigin(root);
    const desiredTransform = `scale(${scale})`;
    const placement = root.getAttribute('data-placement') || '';
    const tipAdaptKey = `${scale}|${placement}|${desiredOrigin}|${desiredTransform}`;
    if (
      root.dataset.agentTipAdaptKey === tipAdaptKey &&
      mount.style.transform === desiredTransform &&
      mount.style.transformOrigin === desiredOrigin &&
      root.style.overflow === 'visible'
    ) {
      return;
    }
    root.dataset.agentTipAdaptKey = tipAdaptKey;
    if (root.style.zoom) root.style.removeProperty('zoom');
    if (root.style.maxWidth) root.style.removeProperty('max-width');
    if (root.style.overflow !== 'visible') root.style.overflow = 'visible';
    if (mount.style.zoom) mount.style.removeProperty('zoom');
    if (mount.style.transformOrigin !== desiredOrigin) {
      mount.style.transformOrigin = desiredOrigin;
    }
    if (mount.style.transform !== desiredTransform) {
      mount.style.transform = desiredTransform;
    }
    delete root.dataset.agentTipNudgeKey;
    nudgeTipRootIntoViewport(root, mount, scale);
    return;
  }

  if (root.style.zoom) root.style.removeProperty('zoom');
  if (root.style.maxWidth) root.style.removeProperty('max-width');
  if (root.style.overflow !== 'visible') root.style.overflow = 'visible';
}

function resetIframeStyle(iframe) {
  iframe.style.removeProperty('width');
  iframe.style.removeProperty('height');
  iframe.style.removeProperty('transform');
  iframe.style.removeProperty('transform-origin');
  iframe.style.removeProperty('max-width');
  iframe.style.removeProperty('max-height');
  iframe.style.removeProperty('box-sizing');
}

function resetDialogBodyAdapt(body) {
  if (!body) return;
  body.style.removeProperty('zoom');
  body.style.overflow = '';
  delete body.dataset.agentIframeAdaptKey;
}

/**
 * 对话 iframe：内部按设备宽布局，再放大铺满弹层
 * 例：外壳 750×H → iframe 设为 (750/scale)×(H/scale)，再 scale(scale)
 *
 * 底部抽屉（is-bottom-sheet）改用 body zoom：iframe 内 @use-gesture + postMessage
 * 跟手拖拽会被 transform:scale 干扰，浮球已用 zoom 规避同类问题。
 */
function adaptDialogIframe(scale) {
  const body = document.querySelector('#agent-chat-root .agent-dialog-body');
  const iframe = body && body.querySelector('iframe');
  const dialog = body?.closest('.agent-dialog');
  if (!body || !iframe) return;

  ensureBodyObserved(body);

  if (scale === 1) {
    resetDialogBodyAdapt(body);
    resetIframeStyle(iframe);
    return;
  }

  const bw = body.clientWidth;
  const bh = body.clientHeight;
  if (bw < 2 || bh < 2) return;

  const isBottomSheet = dialog?.classList.contains('is-bottom-sheet');
  const adaptKey = `${scale}|${Math.round(bw)}|${Math.round(bh)}|${
    isBottomSheet ? 'sheet' : 'dialog'
  }`;
  if (body.dataset.agentIframeAdaptKey === adaptKey) return;

  if (body.style.overflow !== 'hidden') body.style.overflow = 'hidden';

  if (isBottomSheet) {
    resetIframeStyle(iframe);
    iframe.style.boxSizing = 'border-box';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    const zoomVal = String(scale);
    if (body.style.zoom !== zoomVal) body.style.zoom = zoomVal;
    body.dataset.agentIframeAdaptKey = adaptKey;
    return;
  }

  body.style.removeProperty('zoom');
  const iframeW = `${Math.round(bw / scale)}px`;
  const iframeH = `${Math.round(bh / scale)}px`;
  const iframeTransform = `scale(${scale})`;
  iframe.style.boxSizing = 'border-box';
  if (iframe.style.width !== iframeW) iframe.style.width = iframeW;
  if (iframe.style.height !== iframeH) iframe.style.height = iframeH;
  if (iframe.style.maxWidth !== 'none') iframe.style.maxWidth = 'none';
  if (iframe.style.maxHeight !== 'none') iframe.style.maxHeight = 'none';
  if (iframe.style.transform !== iframeTransform) {
    iframe.style.transform = iframeTransform;
  }
  if (iframe.style.transformOrigin !== '0 0') {
    iframe.style.transformOrigin = '0 0';
  }
  body.dataset.agentIframeAdaptKey = adaptKey;
}

function shouldIgnoreAdaptTrigger() {
  return isApplyingAdapt || Date.now() < adaptSuppressUntil;
}

function getViewportAdaptKey() {
  const vv = window.visualViewport;
  const layout = getLayoutWidth();
  return `${layout}|${Math.round(vv?.width || window.innerWidth)}|${Math.round(
    vv?.height || window.innerHeight,
  )}`;
}

function parseInlineStyleLength(styleText, prop) {
  if (!styleText) return null;
  const re = new RegExp(`(?:^|;\\s*)${prop}\\s*:\\s*([^;]+)`, 'i');
  const m = styleText.match(re);
  if (!m) return null;
  const v = Number.parseFloat(m[1]);
  return Number.isFinite(v) ? v : null;
}

/** SDK 晚写浮球 width/height 时触发；left/top 变化不触发 */
function floatInlineSizeChanged(oldStyle, newStyle) {
  const oldW = parseInlineStyleLength(oldStyle, 'width');
  const newW = parseInlineStyleLength(newStyle, 'width');
  const oldH = parseInlineStyleLength(oldStyle, 'height');
  const newH = parseInlineStyleLength(newStyle, 'height');
  return oldW !== newW || oldH !== newH;
}

function ensureFloatSizeObserver() {
  if (!floatSizeObserver) {
    floatSizeObserver = new MutationObserver((records) => {
      if (shouldIgnoreAdaptTrigger()) return;
      for (let i = 0; i < records.length; i += 1) {
        const record = records[i];
        if (record.type !== 'attributes' || record.attributeName !== 'style') {
          continue;
        }
        const oldStyle = record.oldValue || '';
        const newStyle = record.target.getAttribute('style') || '';
        if (floatInlineSizeChanged(oldStyle, newStyle)) {
          scheduleAdapt();
          return;
        }
      }
    });
  }
}

function ensureFloatSizeObserved(floatBtn) {
  ensureFloatSizeObserver();
  if (!floatBtn || floatBtn === observedFloatBtn) return;
  if (observedFloatBtn && floatSizeObserver) {
    try {
      floatSizeObserver.unobserve(observedFloatBtn);
    } catch (e) {
      /* ignore */
    }
  }
  observedFloatBtn = floatBtn;
  floatSizeObserver.observe(floatBtn, {
    attributes: true,
    attributeFilter: ['style'],
    attributeOldValue: true,
  });
}

function unobserveFloatSize() {
  if (floatSizeObserver && observedFloatBtn) {
    try {
      floatSizeObserver.unobserve(observedFloatBtn);
    } catch (e) {
      /* ignore */
    }
  }
  observedFloatBtn = null;
}

function pauseFloatSizeObserver() {
  unobserveFloatSize();
}

function resumeFloatSizeObserver() {
  ensureFloatSizeObserved(document.querySelector('.agent-float-btn'));
}

function observeRoot(root) {
  if (!root || observedRoots.has(root) || !mutationObserver) return;
  observedRoots.add(root);
  mutationObserver.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'hidden', 'data-placement'],
  });
  if (root.classList && root.classList.contains('agent-float-btn')) {
    ensureFloatSizeObserved(root);
  }
}

function reconnectMutationObserver() {
  if (!mutationObserver || !started) return;
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: false,
  });
  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: false,
  });
  observedRoots.forEach((root) => {
    mutationObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'data-placement'],
    });
  });
  collectAgentAdaptRoots().forEach(observeRoot);
  resumeFloatSizeObserver();
}

function pauseMutationObserver() {
  if (mutationObserver) mutationObserver.disconnect();
}

function resumeMutationObserver() {
  reconnectMutationObserver();
}

function pauseDialogResizeObserver() {
  if (resizeObserver && observedBody) {
    try {
      resizeObserver.unobserve(observedBody);
    } catch (e) {
      /* ignore */
    }
  }
}

function resumeDialogResizeObserver() {
  if (resizeObserver && observedBody) {
    try {
      resizeObserver.observe(observedBody);
    } catch (e) {
      /* ignore */
    }
  }
}

function agentDomNeedsAdapt(contentScale, shellScale) {
  const floatEl = document.querySelector('.agent-float-btn');
  if (
    floatEl &&
    shellScale > 1 &&
    floatEl.dataset.agentShellScale !== String(shellScale)
  ) {
    return true;
  }

  const tipRoot = document.querySelector('.agent-tip-root:not([hidden])');
  if (tipRoot && shellScale > 1) {
    const mount =
      tipRoot.querySelector('.agent-tip-mount') ||
      tipRoot.querySelector('.agent-tip');
    if (mount && mount.style.transform !== `scale(${shellScale})`) {
      return true;
    }
  }

  const body = document.querySelector('#agent-chat-root .agent-dialog-body');
  const iframe = body && body.querySelector('iframe');
  if (body && iframe && contentScale > 1) {
    const dialog = body.closest('.agent-dialog');
    const isBottomSheet = dialog?.classList.contains('is-bottom-sheet');
    const adaptKey = `${contentScale}|${Math.round(body.clientWidth)}|${Math.round(
      body.clientHeight,
    )}|${isBottomSheet ? 'sheet' : 'dialog'}`;
    if (body.dataset.agentIframeAdaptKey !== adaptKey) return true;
  }

  return false;
}

/** 弹层高度变化（开合、横竖屏、底部抽屉）时重算 iframe */
function ensureBodyObserved(body) {
  if (!resizeObserver) {
    resizeObserver = new ResizeObserver((entries) => {
      if (shouldIgnoreAdaptTrigger()) return;
      const entry = entries[0];
      if (!entry) return;
      const bodyEl = entry.target;
      const w = Math.round(entry.contentRect.width);
      const h = Math.round(entry.contentRect.height);
      const prevKey = bodyEl.dataset.agentIframeAdaptKey || '';
      if (prevKey && prevKey.includes(`|${w}|${h}|`)) return;
      scheduleAdapt();
    });
  }
  if (observedBody === body) return;
  if (observedBody) {
    try {
      resizeObserver.unobserve(observedBody);
    } catch (e) {
      /* ignore */
    }
  }
  observedBody = body;
  resizeObserver.observe(body);
}

/**
 * 立即按当前视口应用一次适配
 * 【兜底】测量/写 style 异常只打日志，不向外抛（Observer 回调内调用）
 */
export function applyPayAssistantVisualAdapt() {
  if (typeof document === 'undefined') return;
  if (isApplyingAdapt) return;
  try {
    if (document.querySelector('#agent-chat-root .agent-dialog.is-sheet-dragging')) {
      return;
    }
    const contentScale = getPayAssistantVisualScale();
    const shellScale = getShellVisualScale(contentScale);
    const layoutWidth = getLayoutWidth();

    if (
      lastAppliedContentScale === contentScale &&
      lastAppliedShellScale === shellScale &&
      lastAppliedLayoutWidth === layoutWidth &&
      !agentDomNeedsAdapt(contentScale, shellScale)
    ) {
      return;
    }

    isApplyingAdapt = true;
    pauseMutationObserver();
    pauseFloatSizeObserver();
    pauseDialogResizeObserver();
    try {
      adaptFloatButton(shellScale);
      adaptTipBubble(shellScale);
      adaptDialogIframe(contentScale);
      lastAppliedContentScale = contentScale;
      lastAppliedShellScale = shellScale;
      lastAppliedLayoutWidth = layoutWidth;
      lastViewportAdaptKey = getViewportAdaptKey();
      // logSafariFloatDiag('visualAdapt', shellScale);
    } finally {
      isApplyingAdapt = false;
      adaptSuppressUntil = Date.now() + 120;
      resumeMutationObserver();
      resumeFloatSizeObserver();
      resumeDialogResizeObserver();
    }
  } catch (err) {
    console.warn('[PayAssistant] visualAdapt apply failed', err);
  }
}

function scheduleAdapt() {
  if (shouldIgnoreAdaptTrigger()) return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    applyPayAssistantVisualAdapt();
  }, DEBOUNCE_MS);
}

function onViewportChange() {
  const key = getViewportAdaptKey();
  if (key === lastViewportAdaptKey) return;
  scheduleAdapt();
}

/**
 * 【兜底】只收集助手相关根节点
 * 不再 observe 整棵 documentElement subtree，降低充值页 DOM 频繁变动时的卡顿
 */
function collectAgentAdaptRoots() {
  const roots = [];
  const chatRoot = document.getElementById('agent-chat-root');
  const floatBtn = document.querySelector('.agent-float-btn');
  const tipRoot = document.querySelector('.agent-tip-root');
  if (chatRoot) roots.push(chatRoot);
  if (floatBtn) roots.push(floatBtn);
  if (tipRoot) roots.push(tipRoot);
  return roots;
}

/**
 * 启动适配（幂等）。在 SDK create 成功后调用。
 * 监听：浮球/弹层晚挂载、resize、orientation、visualViewport
 *
 * 【兜底】Observer 策略：
 * - body/html 仅 childList（发现晚挂载根节点）
 * - 助手根节点再开 subtree，避免扫整页
 * - 浮球单独监听 style，仅 width/height 变化才 schedule（拖拽改 left/top 不触发）
 */
export function startPayAssistantVisualAdapt() {
  if (started || typeof window === 'undefined') return;
  started = true;

  applyPayAssistantVisualAdapt();
  // 浮球 / iframe 可能晚于 create 才插入 DOM
  setTimeout(applyPayAssistantVisualAdapt, 0);
  setTimeout(applyPayAssistantVisualAdapt, 300);

  mutationObserver = new MutationObserver(() => {
    if (shouldIgnoreAdaptTrigger()) return;
    collectAgentAdaptRoots().forEach(observeRoot);
    scheduleAdapt();
  });

  // 【兜底】仅看 body/html 直接子节点增删（不全树扫描）
  // tip/dialog 在 body 有 zoom 时会挂到 documentElement
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: false,
  });
  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: false,
  });
  collectAgentAdaptRoots().forEach(observeRoot);
  resumeFloatSizeObserver();

  window.addEventListener('resize', onViewportChange);
  window.addEventListener('orientationchange', onViewportChange);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', onViewportChange);
    // iOS Safari：scroll 在地址栏/橡皮筋时高频触发，且不改 layout 宽；仅 resize 即可
    if (!isWebKitSafariHost()) {
      window.visualViewport.addEventListener('scroll', onViewportChange);
    }
  }
  lastViewportAdaptKey = getViewportAdaptKey();
}

/** 销毁助手时还原样式并卸监听，避免泄漏 */
export function stopPayAssistantVisualAdapt() {
  if (!started) return;
  started = false;

  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = null;
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (floatSizeObserver) {
    floatSizeObserver.disconnect();
    floatSizeObserver = null;
  }
  observedBody = null;
  observedFloatBtn = null;
  observedRoots.clear();
  isApplyingAdapt = false;
  adaptSuppressUntil = 0;
  lastAppliedContentScale = null;
  lastAppliedShellScale = null;
  lastAppliedLayoutWidth = null;
  lastViewportAdaptKey = '';

  window.removeEventListener('resize', onViewportChange);
  window.removeEventListener('orientationchange', onViewportChange);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', onViewportChange);
    window.visualViewport.removeEventListener('scroll', onViewportChange);
  }

  const floatEl = document.querySelector('.agent-float-btn');
  if (floatEl) floatEl.style.removeProperty('zoom');

  const tipRoot = document.querySelector('.agent-tip-root');
  if (tipRoot) {
    const tipMount =
      tipRoot.querySelector('.agent-tip-mount') ||
      tipRoot.querySelector('.agent-tip');
    clearTipScale(tipRoot, tipMount);
  }

  const iframe = document.querySelector(
    '#agent-chat-root .agent-dialog-body iframe',
  );
  const body = document.querySelector('#agent-chat-root .agent-dialog-body');
  if (body) resetDialogBodyAdapt(body);
  if (iframe) resetIframeStyle(iframe);
}
